import { subMinutes } from 'date-fns'
import lodash from 'lodash'
import * as sentry from '../../src/utils/sentry'
import { isOnline } from '../Serv/Serv.socketClient'
import * as LaunchDarkly from '../Util/Util.launchdarkly'

/**
 * Params details about the last sync used for sync endpoints so they only sync things that have changed
 */
export type Sync_Params = { lastSyncTime: Date; teams: number[] }

type Response = { syncTime: Date; teams: number[] }

interface StandardSyncArgs<R extends Response> {
    params: {
        get: () => Promise<Sync_Params>
        set: (params: Sync_Params) => Promise<unknown>
    }
    sync: (params: Sync_Params) => Promise<R>
    write: (response: R) => Promise<unknown>
    clear: () => Promise<unknown>
}

export async function Sync_standardSync<R extends Response>(args: StandardSyncArgs<R>) {
    try {
        if (!(await isOnline())) return
        const syncParams = await args.params.get()
        // Our syncing logic has a race condition: an update on the server could generate
        // a 'server sync time' but not immediately commit to the database.  If a sync request
        // is fulfilled in that window before committing, then it will return a newer sync time
        // than the one that was just generated, and so sync will miss that update.
        // To reduce the chance of this happening, we support a configurable 'overlap' interval,
        // i.e. an interval to be deducted from the lastSyncTime that we last received from the server,
        // before it is sent in the sync request.
        // (N.B. this doesn't eliminate the race condition, only reduces the probability of hitting it.)
        const overlapMinutes = LaunchDarkly.getFlagValue('app-sync-overlap-minutes', 0)
        syncParams.lastSyncTime = subMinutes(syncParams.lastSyncTime, overlapMinutes)

        const response = await args.sync(syncParams)
        if (hasTeamsChanged(response.teams, syncParams.teams)) await args.clear()
        await args.write(response)
        await args.params.set({ lastSyncTime: response.syncTime, teams: response.teams })
    } catch (error) {
        sentry.err(error, {})
    }
}

function hasTeamsChanged(teams: number[], oldTeams: number[]) {
    return oldTeams.length > 0 && lodash.xor(teams, oldTeams).length > 0
}
